// Thin client for the same-origin /api. Never hard-codes the domain.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...rest,
    headers: { accept: 'application/json', ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, b.error ?? 'error', b.message ?? `Request failed (${res.status})`, body);
  }
  return body as T;
}

export interface ServerConfig {
  env: string;
  features: { accounts: boolean; billing: boolean; microsoft?: boolean };
  licensePrice: { amount: number; currency: string };
  /** Private test site: whether the signed-in account may use it ('open' = not restricted). */
  access?: 'open' | 'allowed' | 'denied';
  /** Where the high-quality demo video lives; null = the copies bundled in /demo. */
  demoMediaUrl?: string | null;
}

export interface User {
  id: string;
  email: string;
  license: 'none' | 'lifetime';
  /** May manage house ads at /admin. */
  admin?: boolean;
}

export interface FeedItem {
  title: string;
  link: string | null;
  published: string | null;
  summary?: string | null;
  image?: string | null;
}

export interface CalendarEvent {
  uid: string;
  title: string;
  location: string | null;
  start: string;
  end: string;
  allDay: boolean;
  /** Set by provider calendars (Outlook) that carry their own name/colour. */
  calendar?: { id: string; name: string; color: string };
}

export interface OutlookCalendar {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  owner: string | null;
}

export interface DriveFolder {
  id: string;
  name: string;
  childCount: number;
}

/** Work/school: folders shared with the user and SharePoint sites they can open. */
export interface DriveLocations {
  shared: Array<{ drive: string; id: string; name: string }>;
  sites: Array<{ id: string; name: string; url: string | null }>;
}

const query = (params: Record<string, string | undefined>) =>
  new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => e[1] !== undefined)).toString();

export interface DriveListing {
  folder: { id: string | null; name: string; parentId: string | null };
  folders: DriveFolder[];
  mediaCount: number;
}

export interface DriveMedia {
  id: string;
  name: string;
  kind: 'image' | 'video';
  mime: string | null;
  takenAt: string | null;
  width?: number | null;
  height?: number | null;
}

export interface WeatherWarning {
  id: string;
  event: string;
  area: string;
  level: 'yellow' | 'orange' | 'red' | 'unknown';
  severity: string;
  onset: string | null;
  expires: string | null;
  link: string | null;
}

export interface TodoList {
  id: string;
  name: string;
  kind: string;
}

export interface TodoTask {
  id: string;
  title: string;
  done: boolean;
  important: boolean;
  createdAt: string | null;
  completedAt: string | null;
  due: string | null;
}

const todo = (listId: string, taskId?: string) =>
  `/microsoft/todo/lists/${encodeURIComponent(listId)}/tasks${taskId ? `/${encodeURIComponent(taskId)}` : ''}`;

export interface AdminAd {
  id: string;
  advertiser: string;
  headline: string;
  body: string;
  cta: string;
  url: string;
  imageBrief: string | null;
  active: boolean;
  sort: number;
  image: string | null;
}

/** How often free-tier screens show an ad, for how long, and in what order (set at /admin). */
export interface AdSchedule {
  intervalMin: number;
  durationSec: number;
  order: 'random' | 'sequence';
}

export const DEFAULT_AD_SCHEDULE: AdSchedule = { intervalMin: 60, durationSec: 30, order: 'random' };

export type AdInput = Partial<Omit<AdminAd, 'id' | 'image'>> & { image?: string | null };

export const endpoints = {
  ads: () => api<{ ads: AdminAd[]; schedule?: AdSchedule }>('/ads'),
  adminAds: () => api<{ ads: AdminAd[]; schedule: AdSchedule }>('/admin/ads'),
  adminSetAdSchedule: (schedule: AdSchedule) =>
    api<{ schedule: AdSchedule }>('/admin/ads/schedule', { method: 'PUT', json: schedule }),
  adminCreateAd: (ad: AdInput) => api<{ ad: AdminAd }>('/admin/ads', { method: 'POST', json: ad }),
  adminUpdateAd: (id: string, ad: AdInput) => api<{ ad: AdminAd }>(`/admin/ads/${id}`, { method: 'PATCH', json: ad }),
  adminDeleteAd: (id: string) => api<void>(`/admin/ads/${id}`, { method: 'DELETE' }),
  adminImportAds: (ads: AdInput[]) => api<{ added: number }>('/admin/ads/import', { method: 'POST', json: { ads } }),
  todoLists: () => api<{ lists: TodoList[] }>('/microsoft/todo/lists'),
  todoTasks: (listId: string) => api<{ tasks: TodoTask[] }>(todo(listId)),
  todoAdd: (listId: string, title: string) => api<{ task: TodoTask }>(todo(listId), { method: 'POST', json: { title } }),
  todoSetDone: (listId: string, taskId: string, done: boolean) =>
    api<{ task: TodoTask }>(todo(listId, taskId), { method: 'PATCH', json: { done } }),
  todoDelete: (listId: string, taskId: string) => api<void>(todo(listId, taskId), { method: 'DELETE' }),
  warnings: (country: string, place: string) =>
    api<{ local: WeatherWarning[]; elsewhere: number }>(
      `/feeds/warnings?country=${encodeURIComponent(country)}&place=${encodeURIComponent(place)}`,
    ),
  config: () => api<ServerConfig>('/config'),
  me: () => api<{ user: User | null }>('/auth/me'),
  login: (email: string, password: string) => api<{ user: User }>('/auth/login', { method: 'POST', json: { email, password } }),
  register: (email: string, password: string) =>
    api<{ user: User }>('/auth/register', { method: 'POST', json: { email, password } }),
  logout: () => api<void>('/auth/logout', { method: 'POST' }),
  deleteAccount: () => api<void>('/auth/account', { method: 'DELETE' }),
  getSettings: () => api<{ data: Record<string, unknown> | null; version: string | null }>('/settings'),
  putSettings: (data: Record<string, unknown>, baseVersion: string | null) =>
    api<{ version: string }>('/settings', { method: 'PUT', json: { data, baseVersion } }),
  checkout: () => api<{ url: string }>('/billing/checkout', { method: 'POST' }),
  /** Signed link that opens checkout for this account on another device (TVs). */
  phoneLink: () => api<{ url: string; expiresAt: string }>('/billing/phone-link', { method: 'POST' }),
  microsoftStatus: () =>
    api<{ connected: boolean; email: string | null; tasks?: boolean; work?: boolean; workAccess?: boolean }>('/microsoft/status'),
  microsoftDisconnect: () => api<void>('/microsoft', { method: 'DELETE' }),
  /** `drive` omitted = the user's own OneDrive; otherwise a SharePoint library / shared drive. */
  driveChildren: (id?: string, drive?: string, name?: string) =>
    api<DriveListing>(`/microsoft/drive/children?${query({ id, drive, name })}`),
  driveMedia: (folderId: string, recursive: boolean, drive?: string) =>
    api<{ items: DriveMedia[]; truncated: boolean }>(
      `/microsoft/drive/media?${query({ folderId, recursive: recursive ? '1' : '0', drive })}`,
    ),
  driveLocations: () => api<DriveLocations>('/microsoft/drive/locations'),
  siteDrives: (siteId: string) => api<{ drives: Array<{ id: string; name: string }> }>(`/microsoft/sites/${encodeURIComponent(siteId)}/drives`),
  outlookCalendars: () => api<{ calendars: OutlookCalendar[] }>('/microsoft/calendars'),
  outlookCalendar: (days: number, hidden: string[], start?: string) =>
    api<{ events: CalendarEvent[] }>(
      `/microsoft/calendar?days=${days}&hidden=${encodeURIComponent(hidden.join(','))}${start ? `&start=${start}` : ''}`,
    ),
  pairStart: () =>
    api<{ pairingId: string; pollToken: string; code: string; expiresAt: string }>('/pair/start', { method: 'POST' }),
  pairPoll: (pairingId: string, pollToken: string) =>
    api<{ status: 'pending' | 'approved' | 'expired' }>('/pair/poll', { method: 'POST', json: { pairingId, pollToken } }),
  pairApprove: (code: string) => api<{ ok: true }>('/pair/approve', { method: 'POST', json: { code } }),
  preview: (url: string) => api<{ image: string | null }>(`/feeds/preview?url=${encodeURIComponent(url)}`),
  rss: (url: string) => api<{ title: string; items: FeedItem[] }>(`/feeds/rss?url=${encodeURIComponent(url)}`),
  ics: (url: string, days: number, start?: string) =>
    api<{ events: CalendarEvent[] }>(
      `/feeds/ics?url=${encodeURIComponent(url)}&days=${days}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}${start ? `&start=${start}` : ''}`,
    ),
};
